import { Badge } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import Currency from "components/format/currency/currency";
import { btnLinkCls } from "components/tw/classes";
import type { Invoice } from "types/Invoice";
import { InvoiceStatusBadge } from "./order-invoices-table";

/** The id of the column of steps (document, signing, invoice) the strip scrolls to. */
export const ORDER_STEPS_ID = "order-steps";

export type ContractState = "none" | "pending" | "signed" | "annulled";

/**
 * Where the order's contract (its oldest file) stands. Like the order lists,
 * a file is "pending" only once someone was asked to sign it; a generated
 * file nobody was asked to sign still reads "none" (not initiated).
 */
export function contractStateOf(
  contractFile?: {
    isSigned?: boolean;
    annulledAt?: number | string | null;
    hasSigners?: boolean;
  } | null
): ContractState {
  if (!contractFile) return "none";
  if (contractFile.annulledAt) return "annulled";
  if (contractFile.isSigned) return "signed";
  return contractFile.hasSigners ? "pending" : "none";
}

// How each state is worded and coloured; nothing here depends on anything else.
const CONTRACT_BADGES: Record<
  ContractState,
  { labelKey: string; tone: "info" | "attention" | "success" | "critical" }
> = {
  none: { labelKey: "not_initiate", tone: "info" },
  pending: { labelKey: "pending", tone: "attention" },
  signed: { labelKey: "signed", tone: "success" },
  annulled: { labelKey: "annulled_badge", tone: "critical" },
};

type Props = {
  contract: ContractState;
  lastInvoice?: Invoice;
  /** Empty for a draft, which has no number yet. */
  lastInvoiceNumber: string;
};

/**
 * Contract status and the latest invoice, on top of the order page.
 *
 * The page puts the steps column (document, signing, invoice) beside the tabs;
 * where there is no room for that it stacks under the tabs and the comments,
 * so a phone would scroll past everything to learn whether the order is
 * signed. This strip says it first and links down to the steps. Shown only
 * where the columns stack (the layout breaks below about 1100 px beside the
 * sidebar).
 */
export default function OrderStatusStrip({
  contract,
  lastInvoice,
  lastInvoiceNumber,
}: Props) {
  const { t } = useTranslation();
  const contractBadge = CONTRACT_BADGES[contract];

  return (
    <div className="mb-4 rounded-xl border border-zinc-200 bg-white p-3 min-[1100px]:hidden">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="flex items-center gap-2">
          {t("invoice_contract")}:
          <Badge tone={contractBadge.tone}>{t(contractBadge.labelKey)}</Badge>
        </span>
        {lastInvoice && (
          <span className="flex flex-wrap items-center gap-2">
            {t("invoice_last", { defaultValue: "Last" })}:
            {lastInvoiceNumber && <span>{lastInvoiceNumber},</span>}
            <Currency value={lastInvoice._amountWithVat} isSymbol />
            <InvoiceStatusBadge invoice={lastInvoice} />
          </span>
        )}
      </div>
      <button
        type="button"
        className={`${btnLinkCls} mt-1 min-h-11`}
        onClick={() =>
          document
            .getElementById(ORDER_STEPS_ID)
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      >
        {t("order_steps_jump")} ↓
      </button>
    </div>
  );
}
